// Phase 999.4 Plan 06 — Inline Site Config edit row (R4 + R5).
//
// Per-row inline edit form. Renders as a single <tr> so it slots into the
// SiteConfigView <tbody> without breaking table layout. Owns the editing
// state (draft value, validation result, modal open/close, in-flight flag).
//
// R4 (client-side gate): every onChange runs validateByValueClass(draft, row.valueClass).
// Save button is disabled when !validation.ok or busy. Server-side AdminValueClassParser
// (Plan 02) is the authoritative gate; this is UX hygiene.
//
// R5 (warn-before-commit + retain on cancel): clicking Save opens DiffConfirmModal.
// Cancel from modal closes the modal but does NOT touch `draft` — the input
// keeps the unsaved value so the operator can adjust without re-typing.
//
// PUT to /api/admin/sites/{locationId}/options/{optionTypeId} (Plan 03). Response
// body { optionName, before, after } is forwarded to the parent via onSaved(after).

import React, { useState } from 'react';
import { Input, Button } from 'reactstrap';
import { validateByValueClass } from './valueClassValidator';
import { DiffConfirmModal } from './DiffConfirmModal';
import { apiBase } from './apiBase';

export function SiteConfigEdit({ row, locationId, auth, onSaved, onCancel }) {
    const initial = row.currentValue ?? row.defaultValue ?? '';
    const [draft, setDraft]         = useState(initial);
    const [validation, setValid]    = useState(validateByValueClass(initial, row.valueClass));
    const [showModal, setShowModal] = useState(false);
    const [busy, setBusy]           = useState(false);
    const [putError, setPutError]   = useState(null);

    const onChange = (e) => {
        const v = e.target.value;
        setDraft(v);
        setValid(validateByValueClass(v, row.valueClass));
        setPutError(null);
    };

    const onSaveClick = () => {
        if (!validation.ok || busy) return;
        setShowModal(true);
    };

    // R5 acceptance: Cancel closes the modal but keeps `draft` so the input
    // still shows the unsaved value when the modal goes away.
    const onModalCancel = () => {
        setShowModal(false);
    };

    const onConfirm = async () => {
        if (busy) return;
        setBusy(true);
        setPutError(null);
        try {
            let token = '';
            if (auth && typeof auth.getAccessToken === 'function') {
                try { token = auth.getAccessToken(); } catch (e) { /* server will 401 */ }
            }
            const res = await fetch(
                apiBase() + `/api/admin/sites/${locationId}/options/${row.optionTypeId}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: 'Bearer ' + token } : {}),
                    },
                    body: JSON.stringify({ value: draft }),
                }
            );
            if (!res.ok) {
                const errBody = await res.json().catch(() => ({ error: 'HTTP ' + res.status }));
                throw new Error(errBody.error || ('HTTP ' + res.status));
            }
            const body = await res.json();   // { optionName, before, after }
            setShowModal(false);
            onSaved(body.after);
        } catch (e) {
            setPutError(e && e.message ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    };

    const before = row.currentValue ?? '(unset)';

    return (
        <tr data-testid={`site-config-edit-${row.optionTypeId}`}>
            <td>{row.optionName}</td>
            <td className="text-muted small">{row.description}</td>
            <td>
                <Input
                    type="text"
                    value={draft}
                    onChange={onChange}
                    invalid={!validation.ok}
                    disabled={busy}
                    data-testid={`site-config-edit-input-${row.optionTypeId}`}
                />
                {!validation.ok && (
                    <div className="text-danger small" data-testid={`site-config-edit-error-${row.optionTypeId}`}>
                        {validation.error}
                    </div>
                )}
                {putError && (
                    <div className="text-danger small" data-testid={`site-config-edit-puterror-${row.optionTypeId}`}>
                        {putError}
                    </div>
                )}
            </td>
            <td>{row.valueClass}</td>
            <td><span className="text-muted small">editing…</span></td>
            <td>
                <Button
                    color="primary"
                    size="sm"
                    onClick={onSaveClick}
                    disabled={!validation.ok || busy}
                    data-testid={`site-config-save-${row.optionTypeId}`}
                >
                    Save
                </Button>
                {' '}
                <Button
                    color="secondary"
                    size="sm"
                    onClick={onCancel}
                    disabled={busy}
                    data-testid={`site-config-cancel-${row.optionTypeId}`}
                >
                    Cancel
                </Button>
                <DiffConfirmModal
                    isOpen={showModal}
                    label={`${row.optionName} for LocationID ${locationId}`}
                    beforeValue={before}
                    afterValue={draft}
                    onConfirm={onConfirm}
                    onCancel={onModalCancel}
                    busy={busy}
                />
            </td>
        </tr>
    );
}

export default SiteConfigEdit;
